<template>
    <div class="wallet-modal-backdrop wallet-transfer-modal">
        <div class="modal-card" role="dialog" :aria-label="modalTitle">
            <!-- iOS Safari はユーザー操作外の focus() を無視するため、送信タップ時に
                 これへ同期的にフォーカスしてキーボードを開き、認証ステップ表示後に
                 1桁目の入力欄へ移す。 -->
            <input
                ref="authFocusProxy"
                class="auth-focus-proxy"
                type="text"
                inputmode="numeric"
                tabindex="-1"
                aria-hidden="true"
            >
            <div class="modal-header">
                <div class="modal-title-row">
                    <img v-if="showModalLogo" :src="currency.icon" alt="" width="28" height="28">
                    <h2>{{ modalTitle }}</h2>
                </div>
                <button class="icon-button" type="button" :aria-label="$t('userWallet.close')" @click="close">
                    <img src="/assets/images/wallets/close.svg" alt="">
                </button>
            </div>

            <div class="modal-body" v-if="step === 'form'">
                <label class="field">
                    <span>{{ $t('userWallet.transfer.recipientLabel') }}</span>
                    <input
                        ref="recipientInput"
                        type="text"
                        :value="form.recipient"
                        @input="onRecipientInput"
                        :placeholder="$t('userWallet.transfer.recipientPlaceholder')"
                        autocomplete="off"
                        :disabled="submitting"
                    >
                </label>
                <p class="danger-note">{{ $t('userWallet.transfer.mainnetWarning') }}</p>
                <p class="field-error" aria-live="polite">{{ recipientError || '' }}</p>

                <label class="field">
                    <span>{{ $t('userWallet.transfer.amount') }}</span>
                    <span class="amount-field">
                        <input
                            type="text"
                            inputmode="decimal"
                            :value="form.amount"
                            @input="onAmountInput"
                            :placeholder="$t('userWallet.transfer.amountPlaceholder')"
                            autocomplete="off"
                            :disabled="submitting"
                        >
                        <strong>{{ currency.symbol }}</strong>
                        <button type="button" :disabled="submitting" @click="fillMaxAmount">
                            {{ $t('userWallet.transfer.max') }}
                        </button>
                    </span>
                </label>
                <p class="helper-text amount-limit-text" v-html="amountLimitHtml"></p>
                <p class="field-error" aria-live="polite">{{ amountError || '' }}</p>

                <div
                    v-if="estimatedGasEth !== null || gasLoading || gasEstimateError"
                    class="gas-estimate send-gas-estimate"
                    :class="{ 'gas-estimate--loading': gasLoading }"
                    aria-live="polite"
                    :aria-busy="gasLoading"
                >
                    <div>
                        <span>{{ $t('userWallet.transfer.gasLabel') }}</span>
                        <span class="gas-estimate-value">
                            <span class="gas-estimate-spinner" :class="{ 'is-visible': gasLoading }" aria-hidden="true"></span>
                            <strong v-if="estimatedGasEth !== null">{{ formatAmount(estimatedGasEth) }} ETH</strong>
                        </span>
                    </div>
                    <p v-if="estimatedGasEth !== null" class="gas-estimate-note">
                        <span class="info-icon" aria-hidden="true">i</span>
                        {{ $t('userWallet.transfer.gasNote') }}
                    </p>
                    <p v-if="gasQuoteExpired" class="field-error gas-quote-error" aria-live="polite">
                        {{ $t('userWallet.transfer.gasExpired') }}
                    </p>
                    <button
                        v-if="gasQuoteExpired || gasEstimateError || insufficientGas"
                        class="quote-link-button"
                        type="button"
                        :disabled="gasLoading"
                        @click="requoteGas"
                    >
                        {{ $t('userWallet.transfer.requote') }}
                    </button>
                </div>
                <p v-if="insufficientGas && !error" class="field-error" aria-live="polite">
                    {{ $t('userWallet.errors.INSUFFICIENT_ETH_FOR_GAS') }}
                </p>

                <label class="check-row">
                    <input type="checkbox" v-model="form.sendEmailCode" :disabled="submitting">
                    <span>{{ $t('userWallet.transfer.sendEmailCode') }}</span>
                </label>

                <p class="field-error" aria-live="polite">{{ error || '' }}</p>
            </div>

            <div class="modal-body" v-else-if="step === 'confirm'">
                <div class="confirm-list">
                    <div>
                        <span>{{ $t('userWallet.transfer.confirmRecipientLabel') }}</span>
                        <strong>{{ form.recipient }}</strong>
                    </div>
                    <div>
                        <span>{{ $t('userWallet.transfer.confirmAmountLabel', {symbol: currency.symbol}) }}</span>
                        <strong>{{ formatAmount(form.amount) }} {{ currency.symbol }}</strong>
                    </div>
                    <div>
                        <span>{{ $t('userWallet.transfer.gasLabel') }}</span>
                        <strong>{{ formatAmount(estimatedGasEth) }} ETH</strong>
                    </div>
                    <div>
                        <span>{{ $t('userWallet.transfer.authLabel') }}</span>
                        <strong>{{ form.sendEmailCode ? $t('userWallet.transfer.authEnabled') : $t('userWallet.transfer.authDisabled') }}</strong>
                    </div>
                </div>
                <p class="danger-note">{{ $t('userWallet.transfer.mainnetWarning') }}</p>
                <p class="field-error" aria-live="polite">{{ error || '' }}</p>
            </div>

            <div class="modal-body authentication-step" v-else-if="step === 'auth'">
                <div class="auth-code" @paste.prevent="onAuthPaste">
                    <input
                        v-for="(digit, index) in authDigits"
                        :key="index"
                        :ref="'authDigit' + index"
                        type="text"
                        inputmode="numeric"
                        maxlength="1"
                        :aria-label="$t('userWallet.transfer.authDigitLabel', {index: index + 1})"
                        :value="digit"
                        :disabled="submitting"
                        @input="onAuthDigitInput($event, index)"
                        @keydown="onAuthDigitKeydown($event, index)"
                    >
                </div>
                <p class="auth-description" v-html="$t('userWallet.transfer.authDescription')"></p>
                <button class="link-button" type="button" :disabled="submitting" @click="resendCode">
                    {{ $t('userWallet.transfer.resendCode') }}
                </button>
                <p class="field-error" aria-live="polite">{{ authError || '' }}</p>
            </div>

            <div class="modal-body" v-else>
                <div class="completion-mark">
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                        <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                </div>
                <p class="completion-title">{{ $t('userWallet.transfer.completeTitle') }}</p>
                <p class="helper-text">{{ $t('userWallet.transfer.completeDescription') }}</p>
                <div v-if="txHash" class="completion-transaction">
                    <span>{{ $t('userWallet.transfer.txHashLabel') }}</span>
                    <a
                        v-if="explorerTxUrl"
                        :href="explorerTxUrl"
                        :title="txHash"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <strong>{{ txHash }}</strong>
                        <svg aria-hidden="true" viewBox="0 0 24 24">
                            <path d="M14 5h5v5"></path>
                            <path d="M10 14 19 5"></path>
                            <path d="M19 14v5H5V5h5"></path>
                        </svg>
                    </a>
                    <strong v-else>{{ txHash }}</strong>
                </div>
            </div>

            <div 
                class="modal-actions"
                :class="{ 'transfer-confirm-step': step === 'confirm' }"
            >
                <button
                    v-if="step === 'confirm'"
                    class="secondary-button transfer-back-button"
                    type="button"
                    :disabled="submitting"
                    @click="backToForm"
                >{{ $t('userWallet.transfer.back') }}</button>
                <button
                    v-if="step === 'auth'"
                    class="secondary-button"
                    type="button"
                    :disabled="submitting"
                    @click="close"
                >{{ $t('userWallet.transfer.cancel') }}</button>

                <button
                    v-if="step === 'form'"
                    class="primary-button"
                    type="button"
                    :disabled="!canProceedToConfirm || submitting"
                    @click="proceedToConfirm"
                >{{ $t('userWallet.transfer.next') }}</button>
                <button
                    v-if="step === 'confirm'"
                    class="primary-button transfer-next-btn"
                    type="button"
                    :disabled="submitting || gasQuoteExpired"
                    @click="submitTransfer"
                >{{ submitting ? $t('userWallet.transfer.submitting') : $t('userWallet.transfer.submit') }}</button>
                <button
                    v-if="step === 'auth'"
                    class="primary-button"
                    type="button"
                    :disabled="submitting"
                    @click="confirmAuth"
                >{{ submitting ? $t('userWallet.transfer.submitting') : $t('userWallet.transfer.authConfirm') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
    import BN from 'bignumber.js';
    import {v4 as uuidv4} from 'uuid';
    import UserWalletService from '../../../services/UserWalletService';
    import walletFormatMixin from '../../../utils/walletFormatMixin';
    import {CURRENCIES} from '../constants/currencies';
    import config from '../../../../config';

    const MIN_TRANSFER_AMOUNT = 0.0001;
    const GAS_QUOTE_TTL_MS = 2 * 60 * 1000;
    const ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;

    export default {
        name: 'WalletTransferModal',

        mixins: [walletFormatMixin],

        props: {
            symbol: {type: String, required: true},
            walletInfo: {type: Object, default: null},
        },

        data() {
            return {
                step: 'form',
                submitting: false,
                // Idempotency key minted on open and when transfer fingerprint changes; cleared on close.
                // Stable across 戻る → re-confirm when amount/recipient/OTP option/currency unchanged.
                referenceUuid: null,
                form: {
                    recipient: '',
                    amount: '',
                    sendEmailCode: true,
                },
                recipientError: null,
                amountError: null,
                error: null,
                authError: null,
                authDigits: ['', '', '', '', '', ''],
                estimatedGasEth: null,
                gasLoading: false,
                gasEstimateError: false,
                gasQuoteExpired: false,
                gasQuoteTimerId: null,
                transferId: null,
                txHash: null,
            };
        },

        computed: {
            currency() {
                return CURRENCIES[this.symbol] || CURRENCIES.AUBE;
            },

            showModalLogo() {
                return this.step === 'form' || this.step === 'confirm';
            },

            modalTitle() {
                if (this.step === 'auth') {
                    return this.$t('userWallet.transfer.authTitle');
                }
                if (this.step === 'complete') {
                    return this.$t('userWallet.transfer.completeHeading');
                }
                return this.$t('userWallet.transfer.title', {name: this.currency.name});
            },

            explorerTxUrl() {
                if (!config.ethExplorerUrl || !this.txHash) {
                    return null;
                }
                return `${config.ethExplorerUrl.replace(/\/$/, '')}/tx/${this.txHash}`;
            },

            tokenBalance() {
                if (!this.walletInfo || !this.walletInfo.balances) {
                    return '0';
                }
                const key = this.currency.availableBalanceKey || this.currency.balanceKey;
                return this.nonNegativeBalance(this.walletInfo.balances[key] || '0');
            },

            ownAddress() {
                return this.walletInfo && this.walletInfo.wallet
                    ? this.walletInfo.wallet.address
                    : '';
            },

            ethBalance() {
                if (!this.walletInfo || !this.walletInfo.balances) {
                    return '0';
                }
                return this.nonNegativeBalance(
                    this.walletInfo.balances.eth_available
                        || this.walletInfo.balances.eth
                        || '0'
                );
            },

            maxSendAmount() {
                const balance = new BN(this.tokenBalance || 0);
                if (this.symbol === 'ETH' && this.estimatedGasEth !== null) {
                    return BN.max(balance.minus(new BN(this.estimatedGasEth)), 0).toFixed(8, BN.ROUND_DOWN);
                }
                return this.formatAmount(this.tokenBalance);
            },

            amountLimitHtml() {
                const minLabel = this.formatAmount(MIN_TRANSFER_AMOUNT).replace(/\.?0+$/, '');
                return this.$t('userWallet.transfer.amountLimitHtml', {
                    min: minLabel,
                    symbol: this.currency.symbol,
                    max: this.maxSendAmount,
                });
            },

            // Gas不足メッセージは「現在のETH残高」とガス見積もりだけを比較する。
            // ETH送付時も数量(form.amount)は含めない — 数量超過は amountError
            // (insufficientBalance / maxSendAmount) 側で扱う。
            insufficientGas() {
                return this.estimatedGasEth !== null
                    && new BN(this.ethBalance).isLessThan(new BN(this.estimatedGasEth));
            },

            canProceedToConfirm() {
                return this.estimatedGasEth !== null
                    && !this.gasLoading
                    && !this.gasEstimateError
                    && !this.insufficientGas
                    && !this.gasQuoteExpired;
            },

            /** Fields that identity a transfer attempt for reference_uuid stability. */
            transferFingerprint() {
                return [
                    this.symbol,
                    this.form.recipient,
                    this.form.amount,
                    this.form.sendEmailCode ? '1' : '0',
                ].join('|');
            },
        },

        watch: {
            transferFingerprint() {
                this.mintReferenceUuid();
            },
        },

        created() {
            this.mintReferenceUuid();
            this.fetchEstimatedGas();
        },

        mounted() {
            this.$nextTick(() => this.focusRecipientInput());
        },

        beforeDestroy() {
            this.clearGasQuoteTimer();
        },

        methods: {
            mintReferenceUuid() {
                this.referenceUuid = uuidv4();
            },

            focusRecipientInput() {
                const el = this.$refs.recipientInput;
                if (el && el.focus) {
                    el.focus();
                }
            },

            close() {
                if (this.submitting) {
                    return;
                }
                this.referenceUuid = null;
                this.$emit('close', this.step === 'complete');
            },

            onRecipientInput(event) {
                this.form.recipient = event.target.value;
                this.recipientError = null;
            },

            onAmountInput(event) {
                const raw = event.target.value;
                let sanitized = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                const firstDotIndex = sanitized.indexOf('.');
                if (firstDotIndex !== -1) {
                    sanitized = sanitized.slice(0, firstDotIndex + 1)
                        + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
                }
                this.form.amount = sanitized;
                if (raw !== sanitized) {
                    event.target.value = sanitized;
                }
                this.amountError = null;
            },

            fillMaxAmount() {
                this.form.amount = this.maxSendAmount;
                this.amountError = null;
                this.error = null;
            },

            async fetchEstimatedGas() {
                if (this.gasLoading) {
                    return;
                }

                this.gasLoading = true;
                try {
                    const payload = {currency: this.symbol};
                    const recipient = this.form.recipient.trim();
                    if (ADDRESS_PATTERN.test(recipient)) {
                        payload.recipient_address = recipient;
                    }
                    if (this.form.amount) {
                        payload.amount = this.form.amount;
                    }
                    const response = await UserWalletService.getTransferEstimatedGas(payload);
                    this.estimatedGasEth = response.data.estimated_gas_fee;
                    this.gasEstimateError = false;
                    this.gasQuoteExpired = false;
                    this.error = null;
                    this.startGasQuoteTimer();
                } catch (e) {
                    this.clearGasQuoteTimer();
                    this.estimatedGasEth = null;
                    this.gasQuoteExpired = false;
                    this.gasEstimateError = true;
                    this.error = this.$t('userWallet.errors.stakeFailed');
                } finally {
                    this.gasLoading = false;
                }
            },

            requoteGas() {
                if (this.gasLoading) {
                    return;
                }
                this.gasQuoteExpired = false;
                this.gasEstimateError = false;
                this.fetchEstimatedGas();
            },

            clearGasQuoteTimer() {
                if (this.gasQuoteTimerId) {
                    clearTimeout(this.gasQuoteTimerId);
                    this.gasQuoteTimerId = null;
                }
            },

            startGasQuoteTimer() {
                this.clearGasQuoteTimer();
                this.gasQuoteExpired = false;
                this.gasQuoteTimerId = setTimeout(() => {
                    this.gasQuoteTimerId = null;
                    this.gasQuoteExpired = true;
                    if (this.step === 'confirm' || this.step === 'auth') {
                        this.step = 'form';
                        // Gas quote expired while away — refresh balance so the
                        // amount field shows the current available when user re-enters.
                        this.$emit('refresh-wallet');
                    }
                }, GAS_QUOTE_TTL_MS);
            },

            validateForm() {
                this.recipientError = null;
                this.amountError = null;
                this.error = null;

                const recipient = this.form.recipient.trim();
                this.form.recipient = recipient;

                if (!ADDRESS_PATTERN.test(recipient)) {
                    this.recipientError = this.$t('userWallet.transfer.errors.invalidAddress');
                    return false;
                }

                // 自分宛の送付は成立させない。チェーン上は実質no-opだが、EDUの残高は
                // wallet_asset_activities から算出され、完了処理は送信側のSENDしか作らない
                // （相手側のRECEIVEは「別の」管理ウォレット宛の内部送付でのみ作られる）ため、
                // 減っていないはずの残高がSEND分だけ減ってしまう。BE側も
                // TokenTransferService::assertNotSelfTransfer() で同じ条件を拒否する。
                if (this.ownAddress && recipient.toLowerCase() === this.ownAddress.toLowerCase()) {
                    this.recipientError = this.$t('userWallet.transfer.errors.selfTransfer');
                    return false;
                }

                if (!/^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/.test(this.form.amount)) {
                    this.amountError = this.$t('userWallet.errors.INVALID_AMOUNT');
                    return false;
                }

                if (new BN(this.form.amount).isLessThan(MIN_TRANSFER_AMOUNT)) {
                    this.amountError = this.$t('userWallet.transfer.errors.belowMinAmount', {symbol: this.currency.symbol});
                    return false;
                }

                if (new BN(this.form.amount).isGreaterThan(new BN(this.maxSendAmount))) {
                    this.amountError = this.$t('userWallet.transfer.errors.insufficientBalance');
                    return false;
                }

                if (this.insufficientGas) {
                    this.error = this.$t('userWallet.errors.INSUFFICIENT_ETH_FOR_GAS');
                    return false;
                }

                return true;
            },

            proceedToConfirm() {
                // ガス見積もりが未取得・取得失敗・期限切れ・不足のいずれでも確認画面へ進ませない。
                if (!this.canProceedToConfirm) {
                    return;
                }
                if (!this.validateForm()) {
                    this.scrollErrorIntoView();
                    return;
                }
                this.step = 'confirm';
            },

            backToForm() {
                this.error = null;
                this.step = 'form';
                // Refresh available balance so the amount field reflects the
                // current wallet state — another tab may have changed it while
                // the user was on the confirm screen.
                this.$emit('refresh-wallet');
            },

            async submitTransfer() {
                this.error = null;
                if (this.gasQuoteExpired || this.estimatedGasEth === null || this.gasEstimateError) {
                    this.step = 'form';
                    return;
                }

                if (this.form.sendEmailCode) {
                    this.focusAuthProxy();
                }
                this.submitting = true;
                try {
                    if (this.form.sendEmailCode) {
                        const response = await UserWalletService.createTokenTransferV2({
                            currency: this.symbol,
                            recipient_address: this.form.recipient,
                            amount: this.form.amount,
                            reference_uuid: this.referenceUuid,
                        });
                        this.transferId = response.data.transfer_id;
                        this.authDigits = ['', '', '', '', '', ''];
                        this.authError = null;
                        this.step = 'auth';
                        this.$nextTick(() => this.focusAuthDigit(0));
                    } else {
                        const response = await UserWalletService.createTokenTransfer({
                            currency: this.symbol,
                            recipient_address: this.form.recipient,
                            amount: this.form.amount,
                            send_email_code: false,
                            reference_uuid: this.referenceUuid,
                        });
                        this.txHash = response.data.tx_hash;
                        this.clearGasQuoteTimer();
                        this.step = 'complete';
                    }
                } catch (e) {
                    this.blurAuthProxy();
                    this.error = this.mapApiError(e);
                    this.step = 'form';
                    // Refresh available balance so the amount field reflects the
                    // current wallet state — another tab may have reduced it while
                    // this transfer was being submitted.
                    this.$emit('refresh-wallet');
                    this.scrollErrorIntoView();
                } finally {
                    this.submitting = false;
                }
            },

            async confirmAuth() {
                const code = this.authDigits.join('');
                if (!/^\d{6}$/.test(code)) {
                    this.authError = this.$t('userWallet.transfer.errors.codeLength');
                    return;
                }

                this.submitting = true;
                this.authError = null;
                try {
                    const response = await UserWalletService.verifyTokenTransfer({
                        transfer_id: this.transferId,
                        verification_code: code,
                    });
                    this.txHash = response.data.tx_hash;
                    this.clearGasQuoteTimer();
                    this.step = 'complete';
                } catch (e) {
                    const codeName = this.apiErrorCode(e);
                    if (codeName === 'INVALID_VERIFICATION_CODE') {
                        this.authError = this.$t('userWallet.transfer.errors.invalidCode');
                    } else if (codeName === 'VERIFICATION_EXPIRED') {
                        this.authError = this.$t('userWallet.transfer.errors.expiredCode');
                    } else {
                        this.authError = this.mapApiError(e);
                    }
                } finally {
                    this.submitting = false;
                }
            },

            async resendCode() {
                this.authError = null;
                this.authDigits = ['', '', '', '', '', ''];
                this.$nextTick(() => this.focusAuthDigit(0));
                try {
                    await UserWalletService.resendTransferVerifyCode({transfer_id: this.transferId});
                } catch (e) {
                    this.authError = this.mapApiError(e);
                }
            },

            onAuthDigitInput(event, index) {
                const digit = (event.target.value || '').replace(/\D/g, '').slice(-1);
                this.$set(this.authDigits, index, digit);
                event.target.value = digit;
                if (digit && index < 5) {
                    this.focusAuthDigit(index + 1);
                }
            },

            onAuthDigitKeydown(event, index) {
                if (event.key === 'Backspace' && !this.authDigits[index] && index > 0) {
                    this.focusAuthDigit(index - 1);
                }
            },

            onAuthPaste(event) {
                const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
                if (!pasted) {
                    return;
                }
                const next = ['', '', '', '', '', ''];
                pasted.split('').forEach((digit, index) => {
                    next[index] = digit;
                });
                this.authDigits = next;
                this.focusAuthDigit(Math.min(pasted.length, 5));
            },

            focusAuthProxy() {
                const el = this.$refs.authFocusProxy;
                if (el && el.focus) {
                    el.focus({preventScroll: true});
                }
            },

            blurAuthProxy() {
                const el = this.$refs.authFocusProxy;
                if (el && document.activeElement === el) {
                    el.blur();
                }
            },

            focusAuthDigit(index) {
                const refs = this.$refs['authDigit' + index];
                const el = Array.isArray(refs) ? refs[0] : refs;
                if (el && el.focus) {
                    el.focus();
                }
            },

            apiErrorCode(error) {
                return error.response && error.response.data && error.response.data.error
                    ? error.response.data.error.code
                    : null;
            },

            mapApiError(error) {
                const code = this.apiErrorCode(error);
                const transferKey = 'userWallet.transfer.errors.' + code;
                const sharedKey = 'userWallet.errors.' + code;
                if (code && this.$te(transferKey)) {
                    return this.$t(transferKey);
                }
                if (code && this.$te(sharedKey)) {
                    return this.$t(sharedKey);
                }
                return this.$t('userWallet.errors.stakeFailed');
            },

            async scrollErrorIntoView() {
                await this.$nextTick();
                const errorEls = this.$el.querySelectorAll('.field-error');
                for (const el of errorEls) {
                    if (el.textContent && el.textContent.trim()) {
                        el.scrollIntoView({behavior: 'smooth', block: 'center'});
                        break;
                    }
                }
            },
        },
    };
</script>
